/**
 * Google Sign-In button — loads Google Identity Services lazily (only when
 * this component actually mounts, not globally in index.html) and renders
 * Google's own button widget, which hands back a signed ID token via
 * `onSuccess(idToken)`. The caller (Login/Signup) POSTs that token to
 * /auth/google — see auth.service.ts#previewGoogleAuth on the backend.
 *
 * Renders NOTHING if VITE_GOOGLE_CLIENT_ID isn't set, matching the backend's
 * own graceful-degradation posture (POST /auth/google 503s rather than
 * crashing when GOOGLE_CLIENT_ID is unset) — no broken/dead button shown
 * until a real OAuth client id is configured.
 */
import { useEffect, useRef, useState } from 'react';
import { useTheme } from '@/pages/home/marketing-shell';

const GOOGLE_CLIENT_ID = import.meta.env['VITE_GOOGLE_CLIENT_ID'] as string | undefined;
const GSI_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

/** Lets pages skip rendering the "OR" divider around this button entirely
 *  when Google sign-in isn't configured, instead of leaving empty spacing. */
export const isGoogleSignInConfigured = Boolean(GOOGLE_CLIENT_ID);

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
          }) => void;
          renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void;
        };
      };
    };
  }
}

let gsiScriptPromise: Promise<void> | null = null;
/** True once a load attempt has been made — distinguishes "first attempt,
 *  safe to adopt an existing <script> tag" from "retry after a failure",
 *  where any existing tag is a dead one from the failed attempt (its
 *  load/error event already fired once; browsers never replay that for a
 *  newly-attached listener, so reusing it would hang forever). */
let hasAttemptedGsiLoad = false;

/** Loads the GSI script at most once per page, however many buttons mount.
 *  On failure, clears the cached promise so a LATER mount (e.g. the visitor
 *  navigates Login → Signup, or a transient network blip resolves) gets a
 *  fresh attempt instead of the button staying permanently broken for the
 *  rest of the browser session on a stale rejected promise. */
function loadGsiScript(): Promise<void> {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (gsiScriptPromise) return gsiScriptPromise;

  const isRetry = hasAttemptedGsiLoad;
  hasAttemptedGsiLoad = true;

  gsiScriptPromise = new Promise<void>((resolve, reject) => {
    const existing = isRetry ? null : document.querySelector(`script[src="${GSI_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Failed to load Google Sign-In')));
      return;
    }
    if (isRetry) document.querySelector(`script[src="${GSI_SCRIPT_SRC}"]`)?.remove();
    const script = document.createElement('script');
    script.src = GSI_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Sign-In'));
    document.head.appendChild(script);
  }).catch((err) => {
    gsiScriptPromise = null; // allow the next mount to retry instead of reusing this rejection
    throw err;
  });
  return gsiScriptPromise;
}

interface GoogleSignInButtonProps {
  onSuccess: (idToken: string) => void;
  onError?: (message: string) => void;
  /** Google's own button is a fixed-width widget it renders into this div —
   *  full-width parents should still visually center it. */
  width?: number;
}

export default function GoogleSignInButton({ onSuccess, onError, width = 360 }: GoogleSignInButtonProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);
  const [scriptReady, setScriptReady] = useState(false);
  // Every caller of this component (Signup, Login) renders inside
  // marketing-shell's ThemeCtx.Provider, so reading it here — rather than
  // threading a theme prop through every call site — is what lets Google's
  // OWN button widget (not one of our styled elements; GSI paints it into
  // containerRef itself) follow the surrounding page instead of being stuck
  // permanently light/'outline', which read as a jarring white pill on the
  // dark theme.
  const { theme } = useTheme();

  // Load the GSI script and call initialize() exactly once per mount —
  // this is the slow, one-time part (network fetch, cert caching).
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;

    let cancelled = false;
    loadGsiScript()
      .then(() => {
        if (cancelled || !window.google) return;
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (response) => onSuccess(response.credential),
        });
        setScriptReady(true);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setFailed(true);
        onError?.(err.message);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-render the button itself whenever the script becomes ready OR the
  // theme changes — renderButton is cheap (no network I/O, just repaints
  // into containerRef) and simply replaces its previous contents, so
  // toggling theme after the button already rendered updates it in place.
  useEffect(() => {
    if (!scriptReady || !containerRef.current || !window.google) return;
    containerRef.current.innerHTML = '';
    window.google.accounts.id.renderButton(containerRef.current, {
      type: 'standard',
      theme: theme === 'dark' ? 'filled_black' : 'outline',
      size: 'large',
      width,
      text: 'continue_with',
      shape: 'pill',
    });
  }, [scriptReady, theme, width]);

  if (!GOOGLE_CLIENT_ID || failed) return null;

  return <div ref={containerRef} style={{ display: 'flex', justifyContent: 'center' }} />;
}
